import {
    TimeSeriesItem,
    TimeSeriesUnits,
} from "~/modules/token_timeseries/types";
import { formatDate } from "~/utils/functions/formatDate";
import { Emdash } from "~/components/Punctuation/Emdash";
import { DataGridPercentValue } from "~/components/DataGrid/DataGridPercentValue";
import { formatCurrencyValue } from "~/utils/functions/formatCurrencyValue";
import { cn } from "~/lib/utils";
import { formatPercentValue } from "~/utils/functions/formatPercentValue";
import React from "react";

// // // //

function calculatePercentageValues(
    array: Array<TimeSeriesItem & { percentage?: number }>,
) {
    // Calculate the sum of all 'value' fields
    const totalValue = array.reduce((sum, item) => sum + item.points[0][1], 0);

    // Iterate through the array and add the 'percentage' property
    for (let i = 0; i < array.length; i++) {
        const item = array[i];
        const percentage = item.points[0][1] / totalValue;
        array[i].percentage = percentage;
    }

    return array;
}

// // // //

function TooltipRow(props: {
    label: string;
    backgroundColor?: string | null;
    stacked: boolean;
    unit: TimeSeriesUnits;
    percentValue?: number;
    value: number | null;
    currencyValueDecimals?: number;
}) {
    const { label, backgroundColor, stacked, percentValue, unit, value } =
        props;

    return (
        <div className="flex justify-between">
            <p className="flex text-chart-body items-center">
                <span
                    className="h-3 w-3 rounded-full mr-2"
                    style={{
                        backgroundColor: backgroundColor || "#999999",
                    }}
                />
                <span className="mr-2">{label}</span>
            </p>
            <p className="flex items-center text-chart-body ml-4">
                <span className="font-mono">
                    {stacked && (
                        <span className="tabular-nums">
                            {percentValue ? (
                                <DataGridPercentValue value={percentValue} />
                            ) : (
                                <Emdash />
                            )}
                        </span>
                    )}

                    {!stacked && (
                        <>
                            {unit === TimeSeriesUnits.token && (
                                <>
                                    {value ? (
                                        Math.round(value).toLocaleString()
                                    ) : (
                                        <Emdash />
                                    )}
                                </>
                            )}
                            {unit === TimeSeriesUnits.address && (
                                <>
                                    {value ? (
                                        value.toLocaleString()
                                    ) : (
                                        <Emdash />
                                    )}
                                </>
                            )}
                            {unit === TimeSeriesUnits.year && (
                                <>{value ? value + " years" : <Emdash />}</>
                            )}
                            {(unit === TimeSeriesUnits.dollar ||
                                unit === TimeSeriesUnits.dollars) && (
                                <>
                                    {value ? (
                                        "$" +
                                        formatCurrencyValue(
                                            value,
                                            props.currencyValueDecimals,
                                        )
                                    ) : (
                                        <Emdash />
                                    )}
                                </>
                            )}
                            {unit === TimeSeriesUnits.percent && (
                                <>
                                    {value ? (
                                        formatPercentValue(value) + "%"
                                    ) : (
                                        <Emdash />
                                    )}
                                </>
                            )}
                        </>
                    )}
                </span>
            </p>
        </div>
    );
}

// // // //

/**
 * TooltipContent
 * Render content for chart tooltip
 * TODO - add UNIT support to this
 */
export function TooltipContent(props: {
    x: string;
    data: Array<TimeSeriesItem & { percentage?: number }>;
    unit: TimeSeriesUnits;
    stacked: boolean;
    total?: number;
    maxTooltipEntries?: number;
    currencyValueDecimals?: number;
}) {
    const { unit, stacked, maxTooltipEntries } = props;

    const showTotal = props.total !== undefined && props.data.length > 1;
    let data = props.data;

    if (stacked) {
        data = calculatePercentageValues(data);
    }

    const total = props.total ?? 0;

    // Define flag if unit is dollar or dollars
    const isDollar = [TimeSeriesUnits.dollar, TimeSeriesUnits.dollars].includes(
        unit,
    );

    // Limit the number of entries in the tooltip
    const limitMaxEntries =
        maxTooltipEntries && data.length > maxTooltipEntries;

    let dataToRender: Array<TimeSeriesItem & { percentage?: number }> = [
        ...data,
    ];
    let allOthersSum: number = 0;
    let allOthersPercent: number = 0;
    let allOthersEntry: Array<TimeSeriesItem & { percentage?: number }> = [];
    if (limitMaxEntries) {
        dataToRender = data.slice(0, maxTooltipEntries);
        allOthersEntry = data.slice(maxTooltipEntries);

        allOthersSum = allOthersEntry.reduce(
            (total: number, each: TimeSeriesItem & { percentage?: number }) => {
                const pointValue = each.points[0] ? each.points[0][1] : 0;
                return total + pointValue;
            },
            0,
        );

        allOthersPercent = allOthersEntry.reduce(
            (total: number, each: TimeSeriesItem & { percentage?: number }) => {
                const pointValue = each.percentage || 0;
                return total + pointValue;
            },
            0,
        );
    }

    return (
        <div className="flex flex-col select-none p-2 min-w-72">
            <div
                className={cn("flex items-center justify-between mb-2", {
                    "pb-1 border-b": showTotal,
                })}
            >
                <p className="text-chart-lg tabular-nums tracking-wider">
                    {formatDate(props.x)}
                </p>
                {/* ENHANCEMENT - add support for additional units in showTotal */}
                {showTotal && (
                    <div className="flex items-center">
                        <p className="text-chart-body font-mono tabular-nums">
                            {/* Display dollar total */}
                            {isDollar && (
                                <>
                                    {total < 0 && <span>-</span>}
                                    <span>$</span>
                                    {formatCurrencyValue(
                                        Math.abs(total),
                                        props.currencyValueDecimals,
                                    )}
                                </>
                            )}
                            {/* Display non-dollar total */}
                            {!isDollar && <>{total.toLocaleString()}</>}
                        </p>
                    </div>
                )}
            </div>

            {dataToRender.map((d) => {
                const value = d.points[0] ? d.points[0][1] : null;

                return (
                    <TooltipRow
                        key={d.group.id}
                        label={d.group.name}
                        backgroundColor={d.group.color}
                        stacked={stacked}
                        value={value}
                        percentValue={d.percentage}
                        unit={unit}
                        currencyValueDecimals={props.currencyValueDecimals}
                    />
                );
            })}

            {limitMaxEntries && (
                <TooltipRow
                    label="All Others"
                    stacked={stacked}
                    value={allOthersSum}
                    percentValue={allOthersPercent}
                    unit={unit}
                    currencyValueDecimals={props.currencyValueDecimals}
                />
            )}
        </div>
    );
}
